import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { createContact } from "@/lib/api";
import { COMPANY } from "@/lib/data";

export default function ContactConsultation() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consultation: true });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await createContact(form);
      toast.success("Thank you! We'll reach out shortly to schedule your free consultation.");
      setForm({ name: "", email: "", phone: "", message: "", consultation: true });
    } catch {
      toast.error("Could not send. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="consultation-section" id="consultation" className="section-pad bg-navy text-white relative overflow-hidden">
      <div className="absolute inset-0">
        <img src="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/p9qldnae_Screenshot%202026-06-05%20at%209.17.14%E2%80%AFAM.png" alt="" className="w-full h-full object-cover opacity-[0.08]" />
      </div>
      <div className="container-pad mx-auto max-w-7xl relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-medium text-teal-300">
              <span className="w-8 h-px bg-teal-300" />
              Free Consultation
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-white tracking-tight mt-5 leading-[1.15]">
              Talk with our care team — <span className="font-bold text-teal-300">free, no obligation.</span>
            </h2>
            <p className="text-white/70 mt-6 text-lg font-light leading-relaxed">
              Speak with our team about your transportation needs. We'll answer questions, explain our services, and
              help determine the best solution for you or your loved one.
            </p>

            <div className="mt-10 space-y-5">
              <ContactRow icon={Phone} label="Call or Text" value={COMPANY.phone} href={`tel:${COMPANY.phoneRaw}`} />
              <ContactRow icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} />
              <ContactRow icon={MapPin} label="Headquarters" value={COMPANY.address} />
              <ContactRow icon={Clock} label="Scheduling" value={COMPANY.hours} />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              id="contact"
              className="bg-white text-navy rounded-3xl p-8 md:p-10 shadow-2xl"
            >
              <h3 className="font-heading text-2xl font-semibold mb-2">Book Your Free Consultation</h3>
              <p className="text-navy/65 text-sm mb-7">We'll get back to you within one business day.</p>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name *">
                  <Input data-testid="contact-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
                </Field>
                <Field label="Email *">
                  <Input data-testid="contact-input-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                </Field>
                <Field label="Phone" full>
                  <Input data-testid="contact-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(___) ___-____" />
                </Field>
                <Field label="How can we help? *" full>
                  <Textarea
                    data-testid="contact-input-message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your transportation needs..."
                    className="rounded-xl"
                  />
                </Field>
              </div>

              <div className="flex items-start gap-3 mt-5">
                <Checkbox
                  id="consult"
                  data-testid="contact-input-consult"
                  checked={form.consultation}
                  onCheckedChange={(v) => setForm({ ...form, consultation: !!v })}
                />
                <label htmlFor="consult" className="text-sm text-navy/75">I'd like to schedule a free consultation call.</label>
              </div>

              <button type="submit" data-testid="contact-submit" disabled={loading} className="btn-primary mt-7 w-full sm:w-auto disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {loading ? "Sending..." : "Schedule Free Consultation"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-navy font-semibold text-sm">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4 group">
      <div className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-teal-500 transition">
        <Icon className="w-4 h-4 text-teal-300 group-hover:text-white transition" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
        <div className="text-white font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block hover:translate-x-1 transition-transform">{content}</a> : content;
}
